import { useCallback, useRef } from "react";
import { useAuth } from "../contexts/authContext";
import { createAppTrpcClient } from "../trpcClient";

/**
 * Hook for activity data fetching that bypasses ApiContext's enqueueApiCall serializer.
 *
 * ApiContext throws on concurrent calls, but activity browsing naturally triggers
 * concurrent reads (debounced search + load more). This hook calls the tRPC client
 * directly, allowing concurrent read-only queries.
 *
 * Types are inferred from the tRPC AppRouter — no manual duplication.
 */
export function useActivities() {
  const { getAccessTokenAsync } = useAuth();
  const clientRef = useRef<ReturnType<typeof createAppTrpcClient> | null>(null);

  if (clientRef.current === null) {
    clientRef.current = createAppTrpcClient(getAccessTokenAsync);
  }
  const client = clientRef.current;

  const listActivities = useCallback(
    async (params: {
      cursor?: string;
      cursorId?: string;
      limit?: number;
      sportType?: string;
      search?: string;
    }) => {
      return client.activities.list.query(params);
    },
    [client],
  );

  const getActivity = useCallback(
    async (activityId: string) => {
      return client.activities.getById.query({ activityId });
    },
    [client],
  );

  const getCount = useCallback(async () => {
    return client.activities.count.query();
  }, [client]);

  return { listActivities, getActivity, getCount };
}
